/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import {
    Text,
    Stack,
    Divider,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useStyleConfig,
    useAccordionItemState,
} from '@chakra-ui/react'

import PropTypes from 'prop-types'
import SizeRefinements from '@salesforce/retail-react-app/app/pages/product-list/partials/size-refinements'
// import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
// import {FILTER_ACCORDION_SATE} from '@salesforce/retail-react-app/app/constants'
import ColorRefinements from './color-refinements'
import CheckboxRefinements from './checkbox-refinements'
import RadioRefinements from './radio-refinements'
import {ChevronDown, ChevronUp} from '../../../components/icons'

/** Map of refinement attribute IDs to the components used to display values as filter options. */
export const componentMap = {
    cgid: CheckboxRefinements,
    // c_refinementColor: ColorRefinements,
    c_hexColour: ColorRefinements,
    c_size: SizeRefinements,
    price: RadioRefinements,
}

const Refinements = ({
    itemsBefore,
    excludedFilters = [],
    filters = [],
    toggleFilter,
    selectedFilters,
    isLoading,
    isOpen,
}) => {
    // Exclude filters in the exclude list.
    if (excludedFilters) {
        filters = filters.filter(({attributeId}) => !excludedFilters.includes(attributeId))
    }

    const activeFilters = filters.filter((filter) => filter?.values)

    // Getting the indices of filters to open accordions by default
    let filtersIndexes = activeFilters?.map((filter, idx) => idx)

    const [expandedIds, setExpandedIds] = useState([])

    // Use saved state for accordions
    // if (!isServer) {
    //     // TODO: Change this to `useLocalStorage` hook when localStorage detection is more robust
    //     const filterAccordionState = window.localStorage.getItem(FILTER_ACCORDION_SATE)
    //     const savedExpandedAccordionIndexes =
    //         filterAccordionState && JSON.parse(filterAccordionState)

    //     if (savedExpandedAccordionIndexes) {
    //         filtersIndexes = filters
    //             ?.map((filter, index) => {
    //                 if (savedExpandedAccordionIndexes.includes(filter.attributeId)) {
    //                     return index
    //                 }
    //             })
    //             .filter((index) => index !== undefined)
    //     }
    // }

    // Handle saving acccordion state
    // const updateAccordionState = (expandedIndex) => {
    //     const filterState = filters
    //         ?.filter((filter, index) => expandedIndex.includes(index))
    //         .map((filter) => filter.attributeId)

    //     // TODO: Update when localStorage detection is more robust? useLocalStorage is only a getter
    //     window.localStorage.setItem(FILTER_ACCORDION_SATE, JSON.stringify(filterState))
    // }

    const style = useStyleConfig('Refinements')

    const AccordionChevron = () => {
        const {isOpen} = useAccordionItemState()
        return isOpen ? <ChevronUp /> : <ChevronDown />
    }

    let expandedIndexes = activeFilters
        ?.map((filter, index) => {
            if (expandedIds.includes(filter.attributeId)) {
                return index + 1
            } else {
                return null
            }
        })
        .filter(Boolean)

    if (expandedIds.includes('sortby')) {
        expandedIndexes = [0, ...expandedIndexes]
    }

    useEffect(() => {
        if (isOpen && filters && selectedFilters) {
            const toExpand = filters
                .filter((filter) => {
                    const selected = selectedFilters[filter.attributeId]
                    return Array.isArray(selected) ? selected.length > 0 : !!selected
                })
                .map((filter) => filter.attributeId)
            setExpandedIds(toExpand)
        }
    }, [isOpen])

    return (
        <Stack width="100%">
            {/* Wait to have filters before rendering the Accordion to allow the deafult indexes to be accurate */}
            {filtersIndexes && (
                <Accordion
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    onChange={(expandedIndex) => {
                        if (expandedIndex.includes(0)) {
                            setExpandedIds((prev) => [...prev, 'sortby'])
                        } else {
                            setExpandedIds((prev) => prev.filter((id) => id !== 'sortby'))
                        }
                    }}
                    opacity={isLoading ? 0.2 : 1}
                    allowMultiple={true}
                    index={expandedIndexes}
                    reduceMotion={true}
                    variant="filterAccordion"
                >
                    {itemsBefore}

                    {filters?.map((filter) => {
                        // Render the appropriate component for the refinement type, fallback to checkboxes
                        const Values = componentMap[filter.attributeId] || CheckboxRefinements
                        let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []

                        // Catch any non-array values and wrap them in an array
                        if (!Array.isArray(selectedFiltersArray)) {
                            selectedFiltersArray = [selectedFiltersArray]
                        }

                        if (filter.values) {
                            return (
                                <Stack key={filter.attributeId} divider={<Divider />}>
                                    <AccordionItem {...style?.item}>
                                        <>
                                            <AccordionButton
                                                paddingBottom={0}
                                                onClick={() => {
                                                    setExpandedIds((prev) => {
                                                        const newExpandedIds = [...prev]
                                                        if (
                                                            newExpandedIds.includes(
                                                                filter.attributeId,
                                                            )
                                                        ) {
                                                            return newExpandedIds.filter(
                                                                (id) => id !== filter.attributeId,
                                                            )
                                                        }
                                                        return [
                                                            ...newExpandedIds,
                                                            filter.attributeId,
                                                        ]
                                                    })
                                                }}
                                            >
                                                {/*This is the main button */}
                                                <Text {...style?.itemButton}>{filter.label}</Text>
                                                {/* <AccordionIcon /> */}
                                                <AccordionChevron />
                                            </AccordionButton>
                                            <AccordionPanel width="100%" m={0} paddingLeft={0}>
                                                <Values
                                                    selectedFilters={selectedFiltersArray}
                                                    filter={filter}
                                                    toggleFilter={toggleFilter}
                                                />
                                            </AccordionPanel>
                                        </>
                                    </AccordionItem>
                                </Stack>
                            )
                        } else {
                            return <></>
                        }
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

Refinements.propTypes = {
    itemsBefore: PropTypes.arrayOf(PropTypes.element),
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    isOpen: PropTypes.bool,
}

export default Refinements
